import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoadmapTimeline from './components/RoadmapTimeline';
import FeatureFilters from './components/FeatureFilters';
import ProgressOverview from './components/ProgressOverview';
import { features, summarizeFeatures } from './featuresData';
import MilestoneManager from './components/MilestoneManager';
import FeatureDetailsModal from './components/FeatureDetailsModal';

const AdminRoadmap = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [viewMode, setViewMode] = useState('timeline');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setRefreshing(false);
  };

  const formatDateTime = (date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const viewModeOptions = [
    { value: 'timeline', label: 'Timeline', icon: 'Calendar' },
    { value: 'kanban', label: 'Kanban', icon: 'Columns' },
    { value: 'list', label: 'Liste', icon: 'List' }
  ];

  const handleFeatureSelect = (feature) => {
    setSelectedFeature(feature);
  };

  const handleCloseModal = () => {
    setSelectedFeature(null);
  };

  const summary = summarizeFeatures(features);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Roadmap Artis.io</h1>
                <p className="text-muted-foreground">
                  État d&apos;avancement du projet - {formatDateTime(currentTime)}
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* View Mode Selector */}
                <div className="flex items-center space-x-2 bg-card border border-border rounded-lg p-1">
                  {viewModeOptions?.map((option) => (
                    <button
                      key={option?.value}
                      onClick={() => setViewMode(option?.value)}
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        viewMode === option?.value
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={option?.icon} size={16} className="mr-2" />
                      {option?.label}
                    </button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  loading={refreshing}
                >
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Actualiser
                </Button>
              </div>
            </div>
          </div>

          {/* Progress Overview */}
          <div className="mb-8">
            <ProgressOverview />
          </div>

          {/* Filters and Controls */}
          <div className="mb-6">
            <FeatureFilters 
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
            />
          </div>

          {/* Main Content Area */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
            {/* Timeline/Content Area */}
            <div className="lg:col-span-3">
              <RoadmapTimeline 
                viewMode={viewMode}
                selectedFilter={selectedFilter}
                onFeatureSelect={handleFeatureSelect}
              />
            </div>

            {/* Milestone Manager Sidebar */}
            <div className="lg:col-span-1">
              <MilestoneManager />
            </div>
          </div>

          {/* Development Summary Stats */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-card-foreground mb-4">
              Résumé du Développement
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg mx-auto mb-3">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
                <div className="text-2xl font-bold text-card-foreground mb-1">{summary.done}</div>
                <p className="text-sm text-muted-foreground">Fonctionnalités Déployées</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg mx-auto mb-3">
                  <Icon name="Clock" size={24} className="text-warning" />
                </div>
                <div className="text-2xl font-bold text-card-foreground mb-1">{summary.inProgress}</div>
                <p className="text-sm text-muted-foreground">En Développement</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg mx-auto mb-3">
                  <Icon name="Calendar" size={24} className="text-accent" />
                </div>
                <div className="text-2xl font-bold text-card-foreground mb-1">{summary.planned}</div>
                <p className="text-sm text-muted-foreground">Planifiées</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-error/10 rounded-lg mx-auto mb-3">
                  <Icon name="Zap" size={24} className="text-error" />
                </div>
                <div className="text-2xl font-bold text-card-foreground mb-1">{summary.optimization}</div>
                <p className="text-sm text-muted-foreground">Optimisations</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Feature Details Modal */}
      {selectedFeature && (
        <FeatureDetailsModal
          feature={selectedFeature}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default AdminRoadmap;